import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCompanyById, approveCompany, rejectCompany } from 'services/clients.svc'
import { toast } from 'react-toastify'

import './ReviewCompany.scss'
import { BackButton } from 'components'

export default function ReviewCompany() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')

  const { data: company, isLoading } = useQuery(
    ['company', clientId],
    () => getCompanyById(clientId)
  )

  const approveMutation = useMutation(
    () => approveCompany(clientId),
    {
      onSuccess: () => {
        toast.success('Company approved successfully')
        queryClient.invalidateQueries(['pending-companies'])
        navigate('/clients/pending')
      },
      onError: (err) => {
        toast.error('Failed to approve company')
        console.error(err)
      }
    }
  )

  const rejectMutation = useMutation(
    () => rejectCompany(clientId, rejectionReason),
    {
      onSuccess: () => {
        toast.success('Company rejected')
        queryClient.invalidateQueries(['pending-companies'])
        navigate('/clients/pending')
      },
      onError: (err) => {
        toast.error('Failed to reject company')
        console.error(err)
      }
    }
  )

  const handleApprove = () => {
    if (!window.confirm('Are you sure you want to approve this company?')) {
      return;
    }
    approveMutation.mutate()
  }

  const handleReject = () => {
    if (!window.confirm('Are you sure you want to reject this company?')) {
      return;
    }
    rejectMutation.mutate()
    setShowRejectModal(false)
  }

  if (isLoading) {
    return <div aria-busy="true">Loading...</div>
  }

  return (
    <div className="review-company-container">
      <div className="page-header">
        <BackButton />
        <h1>Review Company Registration</h1>
      </div>
      
      <div className="company-details-card">
        <div className="company-header">
          {company.image && (
            <img src={company.image.url} alt="Company logo" className="company-logo" />
          )}
          <h2>{company.name}</h2>
        </div>
        
        <div className="details-grid">
          <div className="detail-section">
            <div className="detail-item">
              <label>ABN</label>
              <span>{company.abn}</span>
            </div>
            <div className="detail-item">
              <label>Builders Registration Number</label>
              <span>{company.buildersRegNumber}</span>
            </div>
            <div className="detail-item">
              <label>Office Phone</label>
              <span>{company.phone}</span>
            </div>
            <div className="detail-item">
              <label>Office Email</label>
              <span>{company.email}</span>
            </div>
            <div className="detail-item">
              <label>Office Address</label>
              <span>{company.address}</span>
            </div>
            <div className="detail-item">
              <label>Postal Address</label>
              <span>{company.postalAddress}</span>
            </div>
            <div className="detail-item">
              <label>Submitted</label>
              <span>{company.createdAt ? new Date(company.createdAt).toLocaleString() : '—'}</span>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <span className="status-badge pending">Pending Review</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="actions-section">
        <h3>Review Actions</h3>
        <div className="action-buttons">
          <button 
            onClick={() => navigate(`/clients/${clientId}/edit`)}
            className="secondary"
          >
            Edit Details
          </button>
          
          <button 
            onClick={handleApprove}
            className="approve-button"
            disabled={approveMutation.isLoading}
            aria-busy={approveMutation.isLoading}
          >
            ✓ Approve Company
          </button>
          
          <button 
            onClick={() => setShowRejectModal(true)}
            className="reject-button"
            disabled={rejectMutation.isLoading}
          >
            ✗ Reject Application
          </button>
        </div>
      </div>
      
      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal reject-modal-section">
            <h3>Reject Company Registration</h3>
            <p className="reject-helper-text">Please provide a reason for rejecting this application:</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Provide a clear reason for rejection..."
              rows={4}
              className="rejection-textarea"
            />
            <div className="modal-actions">
              <button 
                onClick={() => setShowRejectModal(false)}
                className="secondary"
              >
                Cancel
              </button>
              <button 
                onClick={handleReject}
                disabled={rejectMutation.isLoading}
                aria-busy={rejectMutation.isLoading}
                className="reject-button"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}